/**
 * Sign-up reducer — pure function: `(state, action) => state`.
 *
 * Outer switch is on `state.step` so each step reads as a self-contained
 * node in the state machine: "when we are HERE, these transitions are legal."
 *
 * Returns the same reference when the action is not valid in the current step.
 * FlowStore uses reference equality to skip notification on no-op transitions.
 */

import type { SignUpState, SignUpAction, SignUpCreateParams, SignUpPendingFields } from './types';

export const INITIAL_STATE: SignUpState = { step: 'IDLE', email: null, pendingFields: null, error: null };

export function stripPassword(params: SignUpCreateParams): SignUpPendingFields {
  const { password: _, ...rest } = params;
  return rest;
}

export function signUpReducer(state: SignUpState, action: SignUpAction): SignUpState {
  if (action.type === 'RESET') return INITIAL_STATE;

  switch (state.step) {
    // ── IDLE ──────────────────────────────────────────────────────────────
    case 'IDLE':
      switch (action.type) {
        case 'SUBMIT_REGISTRATION':
          return {
            step: 'REGISTERING',
            email: action.params.emailAddress,
            pendingFields: stripPassword(action.params),
            pendingProps: action.params,
          };
        default:
          return state;
      }

    // ── CONSENT_REQUIRED ─────────────────────────────────────────────────
    case 'CONSENT_REQUIRED':
      switch (action.type) {
        case 'SUBMIT_CONSENT':
          return {
            step: 'REGISTERING_AFTER_CONSENT',
            email: state.email,
            pendingFields: state.pendingFields,
            pendingProps: { ...state.pendingProps, newsletter: action.newsletterConsent },
          };
        case 'BACK':
          return { step: 'IDLE', email: state.email, pendingFields: state.pendingFields, error: null };
        default:
          return state;
      }

    // ── REGISTERING (direct path) ────────────────────────────────────────
    case 'REGISTERING':
      switch (action.type) {
        case 'SIGN_UP_OK':
          return { step: 'COMPLETE', email: state.email };
        case 'SIGN_UP_NEEDS_VERIFICATION':
          return {
            step: 'AWAITING_VERIFICATION',
            email: state.email,
            pendingFields: state.pendingFields,
            pendingProps: state.pendingProps,
            error: null,
          };
        case 'SIGN_UP_INCOMPLETE':
          return {
            step: 'IDLE',
            email: state.email,
            pendingFields: state.pendingFields,
            error: 'Registration completed but auto-sign-in failed. Please sign in.',
          };
        case 'SIGN_UP_FAIL':
          return { step: 'IDLE', email: state.email, pendingFields: state.pendingFields, error: action.error };
        case 'BACK':
          return { step: 'IDLE', email: state.email, pendingFields: null, error: null };
        default:
          return state;
      }

    // ── REGISTERING_AFTER_CONSENT ────────────────────────────────────────
    case 'REGISTERING_AFTER_CONSENT':
      switch (action.type) {
        case 'SIGN_UP_OK':
          return { step: 'COMPLETE', email: state.email };
        case 'SIGN_UP_NEEDS_VERIFICATION':
          return {
            step: 'AWAITING_VERIFICATION',
            email: state.email,
            pendingFields: state.pendingFields,
            pendingProps: state.pendingProps,
            error: null,
          };
        case 'SIGN_UP_INCOMPLETE':
          return {
            step: 'IDLE',
            email: state.email,
            pendingFields: state.pendingFields,
            error: 'Registration completed but auto-sign-in failed. Please sign in.',
          };
        case 'SIGN_UP_FAIL':
          return { step: 'IDLE', email: state.email, pendingFields: state.pendingFields, error: action.error };
        case 'CONSENT_FAIL':
          return {
            step: 'CONSENT_REQUIRED',
            email: state.email,
            pendingFields: state.pendingFields,
            pendingProps: state.pendingProps,
            requiredFields: [{ key: 'consent', label: 'Privacy & Terms' }],
            error: action.error,
          };
        case 'BACK':
          return { step: 'IDLE', email: state.email, pendingFields: null, error: null };
        default:
          return state;
      }

    // ── AWAITING_VERIFICATION ────────────────────────────────────────────
    case 'AWAITING_VERIFICATION':
      switch (action.type) {
        case 'SUBMIT_VERIFICATION':
          return { step: 'VERIFYING', email: state.email, pendingProps: state.pendingProps };
        case 'RESEND_CODE_FAIL':
          return { ...state, error: action.error };
        default:
          return state;
      }

    // ── VERIFYING ────────────────────────────────────────────────────────
    case 'VERIFYING':
      switch (action.type) {
        case 'VERIFICATION_OK':
          return { step: 'COMPLETE', email: state.email };
        case 'VERIFICATION_NEEDS_SIGN_IN':
          return { step: 'SIGN_IN_REQUIRED', email: state.email, error: null };
        case 'VERIFICATION_FAIL':
          return {
            step: 'AWAITING_VERIFICATION',
            email: state.email,
            pendingFields: stripPassword(state.pendingProps),
            pendingProps: state.pendingProps,
            error: action.error,
          };
        default:
          return state;
      }

    // ── COMPLETE ─────────────────────────────────────────────────────────
    case 'COMPLETE':
      switch (action.type) {
        case 'SUBMIT_PHONE':
          return { step: 'SUBMITTING_PHONE', email: state.email };
        case 'BACK':
          return { step: 'IDLE', email: state.email, pendingFields: null, error: null };
        default:
          return state;
      }

    // ── SUBMITTING_PHONE ─────────────────────────────────────────────────
    case 'SUBMITTING_PHONE':
      switch (action.type) {
        case 'PHONE_OK':
          return { step: 'COMPLETE', email: state.email };
        case 'PHONE_FAIL':
          return { step: 'SIGN_IN_REQUIRED', email: state.email, error: action.error };
        case 'BACK':
          return { step: 'IDLE', email: state.email, pendingFields: null, error: null };
        default:
          return state;
      }

    // ── SIGN_IN_REQUIRED ─────────────────────────────────────────────────
    case 'SIGN_IN_REQUIRED':
      switch (action.type) {
        case 'SUBMIT_REGISTRATION':
          return {
            step: 'REGISTERING',
            email: action.params.emailAddress,
            pendingFields: stripPassword(action.params),
            pendingProps: action.params,
          };
        case 'BACK':
          return { step: 'IDLE', email: state.email, pendingFields: null, error: null };
        default:
          return state;
      }

    default:
      return state;
  }
}
